import {useCallback, useContext, useEffect, useState} from 'react';
import CommandsContext from '../context/commandsContext';
import MenuContext from '../context/menuContext';
import ValuesContext from '../context/valuesContext';
import {generateAlertsChildren} from './useCommonMenu';

export const findParent = (parent, list, selected) => {
  for (const item of list) {
    if (item.id === selected.id) {
      return parent;
    }

    if (item.children) {
      const parent = findParent(item, item.children, selected);
      if (parent) {
        return parent;
      }
    }
  }

  return null;
};

export const useMenu = () => {
  const {
    selected,
    setSelected,
    setList,
    setItems,
    items,
    list,
    alert,
    showInfo,
    component,
    disableMenu,
    onComponent,
    onShowMenu,
  } = useContext(MenuContext);
  const {values, onUpdateValues} = useContext(ValuesContext);
  const {onUpdateCommands} = useContext(CommandsContext);
  const [tapCount, setTapCount] = useState(0);

  const onUp = useCallback(() => {
    setTapCount(0);
    if (disableMenu) return;

    const index = list.findIndex(item => item.id === selected?.id);
    const nextItem = list[index - 1];
    if (!nextItem) {
      return;
    }
    setSelected(nextItem);
  }, [component, disableMenu, list, selected, items, setSelected]);

  const onDown = useCallback(() => {
    setTapCount(0);

    if (disableMenu) return;

    const index = list.findIndex(item => {
      return item.id === selected?.id;
    });
    const nextItem = list[index + 1];
    if (!nextItem) {
      return;
    }

    setSelected(nextItem);
  }, [component, disableMenu, list, selected, items, setSelected]);

  const onLeft = useCallback(() => {
    setTapCount(0);

    if (disableMenu) return;

    if (list.length === 0) {
      return;
    }

    if (showInfo) {
      return;
    }

    const parent = findParent(null, items, selected);
    if (!parent) {
      onShowMenu(false);
      return;
    }

    setSelected(parent);
  }, [component, disableMenu, list, selected, items, setSelected]);

  const onRight = useCallback(() => {
    if (disableMenu) return;

    if (list.length === 0) {
      onShowMenu(true);
      return;
    }

    if (selected?.component) {
      onComponent(selected.component);
      return;
    }

    if (selected?.isAlert) {
      const [newItems, alerts] = generateAlertsChildren(items, alert);
      setItems(newItems);
      setSelected(alerts.children[0]);
      return;
    }

    if (!selected?.children && !selected?.component) {
      if (tapCount === 0 && selected.requireDoubleRight) {
        setTapCount(1);
        return;
      }

      const parent = findParent(null, items, selected);

      if (tapCount === 1) {
        onUpdateValues(parent.id, !selected.subName ? selected.name : selected.subName);
        onUpdateValues(
          parent.id === 5321
            ? 532
            : parent.id && parent.id === 5331
            ? 533
            : parent.id && parent.id === 5341
            ? 534
            : parent.id,
          !selected.subName ? selected.name : selected.subName,
        );
        setTapCount(2);
      }

      setSelected(parent);
      return;
    }

    const current = selected.children.findIndex(child => child.name === values[selected.id]);
    setSelected(selected.children[current === -1 ? 0 : current]);
  }, [component, values, tapCount, disableMenu, list, selected, items]);

  useEffect(() => {
    if (!component) {
      setTimeout(() => {
        onUpdateCommands({onUp, onDown, onRight, onLeft});
      });
    }
  }, [component, onUp, onDown, onRight, onLeft]);

  return {
    selected,
    setSelected,
    items,
    list,
    alert,
    setItems,
    setList,
    onComponent,
    tapCount,
    onUp,
    onDown,
    onRight,
    onLeft,
  };
};
